import React, { useRef, useState } from "react";

import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";

import { ReactComponent as Banner } from "../../../Assets/Images/BannerIconBlue.svg";
import Label from "../../atoms/Input/Label";

export const BannerUpload = ({
  control,
  setValue,
  watch,
  setError,
  label,
  required,
  error,
  helperText,
}) => {
  const { t } = useTranslation();
  const [bannerError, setBannerError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const bannerFile = watch("bannerImage");

  const previewUrl =
    bannerFile && typeof bannerFile === "object"
      ? URL.createObjectURL(bannerFile)
      : bannerFile;

  const handleFileProcess = (file) => {
    if (!file) {
      setBannerError(t("validation:required-field"));
      setError?.("bannerImage", {
        type: "manual",
        message: t("validation:required-field"),
      });
      return;
    }

    if (!file.type.startsWith("image/")) {
      const error = "Invalid format. Please try again.";
      setBannerError(error);
      setError?.("bannerImage", { type: "manual", message: error });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      const error = t(
        "File size exceeds 2MB limit. Please upload a smaller file.",
        { size: "2MB" },
      );
      setBannerError(error);
      setError?.("bannerImage", { type: "manual", message: error });
      return;
    }

    setValue("bannerImage", file);
    setBannerError(null);
    setError?.("bannerImage", null);
  };

  const handleBannerUpload = (e) => {
    setBannerError(null);
    const file = e.target.files?.[0];
    handleFileProcess(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setBannerError(null);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileProcess(files[0]);
    } else {
      setBannerError(t("validation:required-field"));
      setError?.("bannerImage", {
        type: "manual",
        message: t("validation:required-field"),
      });
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Stack spacing={1} width="100%">
      <Label label={label} required={required} />
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          backgroundColor: "#f0f8ff",
          padding: "24px",
          borderRadius: "8px",
          textAlign: "center",
          border: isDragOver ? "2px  #2196F3" : "2px  #cccdffff",
          cursor: "pointer",
          minHeight: "120px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
          position: "relative",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          accept="image/png, image/jpeg, image/jpg"
          onChange={handleBannerUpload}
        />

        {!bannerFile ? (
          <div style={{ display: "flex", alignItems: "stretch", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "stretch" }}>
              <Banner style={{ height: "70px", width: "70px" }} />
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: "500",
                  textAlign: "left",
                }}
              >
                <span style={{ color: "#2196F3", textDecoration: "underline" }}>
                  Click to upload
                </span>{" "}
                or drag and drop
              </p>
              <p
                style={{
                  margin: "0 0 4px 0",
                  fontSize: "12px",
                  color: "#666",
                  textAlign: "left",
                }}
              >
                File types: .png, .jpg, .jpeg
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "12px",
                  color: "#666",
                  textAlign: "left",
                }}
              >
                File size: 2MB
              </p>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <img
              src={previewUrl}
              alt="Banner preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100px",
                objectFit: "contain",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            />
            <p
              style={{
                margin: "8px 0 0 0",
                fontSize: "12px",
                color: "#666",
              }}
            >
              Click to replace image
            </p>
          </div>
        )}
      </div>

      {(bannerError || error) && (
        <div
          style={{
            color: "#FF3B30",
            fontSize: "12px",
            marginTop: "4px",
            padding: "4px 0",
          }}
        >
          {error ? helperText : bannerError}
        </div>
      )}
    </Stack>
  );
};
